/**
 * PDF en tareas: regla de producto para no restringir a tenants que no usan roles custom.
 *
 * - Quien tenga algún `customRole:…` en roles: solo ve exportar PDF si `taskPdfDownload` está en
 *   featureKeys (no cuenta un rol fijo extra como ejecutor). Excepción: `admin`.
 * - Sin customRole: quien tenga rol en TAREA_READ_LEGACY_ROLES_FOR_PDF sigue viendo exportar aunque
 *   featureKeys esté vacío; el resto necesita `taskPdfDownload`.
 * - Si el contexto no cargó (`failed` en el store): `canShowTaskPdfExportForUi` relaja el gate (legado).
 *
 * Constante alineada con stem-web-backend CustomRoleFeatureKeys.taskPdfDownload.
 */
import {isCustomRoleToken} from 'src/security/customRoleConstants';
import { CustomRoleFeatureKeys } from 'src/security/customRoleFeatureKeys';

const TASK_PDF_DOWNLOAD = 'taskPdfDownload';

/**
 * Mantener alineado con stem-web-backend Permissions.values.tareaRead.allowedRoles
 * (roles con lectura de tarea “clásica” pueden exportar PDF sin taskPdfDownload).
 */
export const TAREA_READ_LEGACY_ROLES_FOR_PDF: readonly string[] = [
  'admin',
  'administrador',
  'administradorOperaciones',
  'administradorRecursosHumanos',
  'administradorInventario',
  'administradorCatalogo',
  'administradorSolicitudes',
  'planificadorOperaciones',
  'planificadorProcedimientos',
  'aceptadorPlanificaciones',
  'validadorPlanificaciones',
  'ejecutor',
  'signingUser',
  'controlInicioActividad',
  'editorProgreso',
  'ejecutorProgreso',
  'reasignadorDeTareas',
  'kanbanAdmin',
  'visualizadorDeActividades',
  'dashboard',
];

export function canShowTaskPdfExport(
  currentRoles: string[],
  featureKeys: string[],
): boolean {
  if (currentRoles.includes('admin')) {
    return true;
  }
  const hasCustomRole = currentRoles.some((r) => isCustomRoleToken(r));
  if (hasCustomRole) {
    return (
      featureKeys.includes(TASK_PDF_DOWNLOAD) ||
      featureKeys.includes(CustomRoleFeatureKeys.taskKanbanGestor)
    );
  }
  const legacy = currentRoles.some((r) => TAREA_READ_LEGACY_ROLES_FOR_PDF.includes(r));
  if (legacy) {
    return true;
  }
  return featureKeys.includes(TASK_PDF_DOWNLOAD);
}

/**
 * Si el GET tenant-context falló (404/red), el PDF vuelve al criterio previo a featureKeys (solo estado
 * de tarea en la pantalla). Con contexto cargado bien, se aplica gate estricto.
 * La descarga real sigue validándose en el servidor.
 */
export function canShowTaskPdfExportForUi(
  currentRoles: string[],
  featureKeys: string[],
  tenantAccessContextLoadStatus: 'loaded' | 'failed' | null | undefined,
): boolean {
  if (tenantAccessContextLoadStatus === 'failed') {
    return true;
  }
  return canShowTaskPdfExport(currentRoles, featureKeys);
}
